import { cn } from '@/lib/cn';
import {
  deviceTimeZone,
  effectiveTimeZone,
  formatCalendarDate,
  formatDate,
  formatDateTime,
  formatTime,
  formatWith,
  isDateOnly,
  utcOffsetLabel,
} from '@/lib/datetime';
import { relativeTime } from '@/lib/relative-time';
import { For, type JSX, Show, createSignal, createUniqueId } from 'solid-js';
import { Portal } from 'solid-js/web';
import { Motion } from 'solid-motionone';

export type DateTextFormat = 'date' | 'time' | 'datetime' | 'relative';

export interface DateTextProps {
  /** ISO instant, or a calendar date (YYYY-MM-DD) which is never timezone-shifted. */
  value: string | null | undefined;
  /** Preset (default `datetime`) or an explicit dayjs token. */
  format?: DateTextFormat | (string & {});
  /** Zone used only while the user's timezone preference is Auto (e.g. Bangkok on runner pages). */
  fallbackTimeZone?: string;
  /** Reference clock for `relative`, so callers can make it tick. */
  now?: number;
  /** Text when value is empty. */
  empty?: string;
  class?: string;
}

const TOOLTIP_TOKEN = 'ddd D MMM YYYY HH:mm:ss';
const GAP = 8;

/**
 * A date in the user's preferred format; hover or focus shows the same instant
 * in the preferred zone, the device zone (when different) and UTC, the raw value,
 * and a relative time (ported from life-memo's DateText).
 */
export const DateText = (props: DateTextProps): JSX.Element => {
  const [rect, setRect] = createSignal<DOMRect | null>(null);
  const [tipWidth, setTipWidth] = createSignal(0);
  const tipId = createUniqueId();
  const opts = () => ({ fallbackTimeZone: props.fallbackTimeZone });
  const value = () => props.value ?? '';

  const text = () => {
    const v = value();
    const format = props.format ?? 'datetime';
    if (isDateOnly(v) && (format === 'date' || format === 'datetime')) {
      return formatCalendarDate(v);
    }
    switch (format) {
      case 'date':
        return formatDate(v, opts());
      case 'time':
        return formatTime(v, opts());
      case 'datetime':
        return formatDateTime(v, opts());
      case 'relative':
        return relativeTime(v, props.now);
      default:
        return formatWith(v, format, effectiveTimeZone(opts()));
    }
  };
  const hasValue = () => text() !== '';

  // One row per distinct zone: preferred, device, UTC — then the raw value.
  const rows = () => {
    const v = value();
    if (!hasValue() || isDateOnly(v)) return [];
    const zones = [effectiveTimeZone(opts()), deviceTimeZone, 'UTC'];
    const labels = ['', 'Your device', ''];
    const zoneRows = zones
      .map((tz, i) => ({ tz, label: labels[i] || tz }))
      .filter((z, i) => zones.indexOf(z.tz) === i)
      .map((z) => ({
        label: z.label,
        offset: utcOffsetLabel(z.tz, v),
        value: formatWith(v, TOOLTIP_TOKEN, z.tz),
        raw: false,
      }));
    return [...zoneRows, { label: 'Original', offset: '', value: v, raw: true }];
  };

  const show = (e: Event) => setRect((e.currentTarget as HTMLElement).getBoundingClientRect());
  const hide = () => setRect(null);

  // Centred above the text (below when there is no room), with the whole box kept on screen.
  const position = (): JSX.CSSProperties => {
    const r = rect();
    if (!r) return {};
    const above = r.top > 120;
    const left = r.left + r.width / 2 - tipWidth() / 2;
    return {
      left: `${Math.max(GAP, Math.min(left, window.innerWidth - tipWidth() - GAP))}px`,
      top: above ? `${r.top - GAP}px` : `${r.bottom + GAP}px`,
      transform: above ? 'translateY(-100%)' : undefined,
    };
  };
  const duration = () => (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 0.15);

  return (
    <>
      <Show when={hasValue()} fallback={<span class={props.class}>{props.empty ?? '—'}</span>}>
        <span
          tabindex="0"
          aria-describedby={rect() ? tipId : undefined}
          class={cn('cursor-help tabular-nums', props.class)}
          onMouseEnter={show}
          onMouseLeave={hide}
          onFocus={show}
          onBlur={hide}
        >
          {text()}
        </span>
      </Show>
      <Show when={rect()}>
        <Portal>
          <Motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: duration() }}
            id={tipId}
            role="tooltip"
            ref={(el: HTMLDivElement) => queueMicrotask(() => setTipWidth(el.offsetWidth))}
            class="pointer-events-none fixed z-[60] max-w-[calc(100vw-16px)] break-all rounded-lg bg-gray-900 px-3 py-2 text-xs text-white shadow-lg sm:whitespace-nowrap sm:break-normal"
            style={position()}
          >
            <Show
              when={rows().length > 0}
              fallback={
                <div class="font-medium">{formatCalendarDate(value(), 'dddd D MMMM YYYY')}</div>
              }
            >
              <table>
                <tbody>
                  <For each={rows()}>
                    {(row) => (
                      <tr>
                        <td class="pr-3 text-gray-400">
                          {row.label}
                          <Show when={row.offset}>
                            {' '}
                            <span class="text-gray-500">({row.offset})</span>
                          </Show>
                        </td>
                        <td
                          class={cn(
                            'tabular-nums',
                            row.raw ? 'font-mono text-gray-300' : 'font-medium',
                          )}
                        >
                          {row.value}
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
              <div class="mt-1 border-t border-gray-700 pt-1 text-gray-400">
                {relativeTime(value(), props.now)}
              </div>
            </Show>
          </Motion.div>
        </Portal>
      </Show>
    </>
  );
};
